// src/pages/PublicArticlePage.js
import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { articlesService } from '../Services/articlesService';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Sparkles,
  MessageCircle,
  FileSearch,
  Phone,
} from 'lucide-react';
import SEO from '../components/SEO';
import '../styles/article-content.css';

const LOGO_URL = '/RemaH.png';

const PublicArticlePage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [articleId]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const data = await articlesService.getArticleById(articleId);
      if (!data || !data.published) {
        navigate('/articles');
        return;
      }
      setArticle(data);
    } catch (error) {
      console.error('Erreur chargement article:', error);
      navigate('/articles');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <PublicNav />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={article.title}
        description={article.description || article.title}
        path={`/articles/${articleId}`}
        image={article.thumbnail || article.images?.[0] || '/RemaH.png'}
        type="article"
        keywords={article.tags?.join(', ')}
      />

      <PublicNav />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Retour */}
        <button
          onClick={() => navigate('/articles')}
          className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Tous les articles
        </button>

        {/* Article */}
        <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 overflow-hidden">
          {/* Image bannière */}
          {(article.thumbnail || article.images?.[0]) && (
            <img
              src={article.thumbnail || article.images[0]}
              alt={article.title}
              className="w-full h-72 object-cover"
            />
          )}

          <div className="p-6 lg:p-8">
            {/* Tags */}
            {article.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Titre */}
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap gap-4 mb-6 text-gray-500 dark:text-gray-400 text-sm">
              {article.difficulty && (
                <span
                  className={`px-2 py-1 rounded ${
                    article.difficulty === 'Facile'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : article.difficulty === 'Moyen'
                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }`}
                >
                  {article.difficulty}
                </span>
              )}
              {article.createdAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(article.createdAt.seconds * 1000).toLocaleDateString('fr-FR')}
                </span>
              )}
            </div>

            {/* Description */}
            {article.description && (
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
                {article.description}
              </p>
            )}

            {/* Contenu */}
            <div
              className="article-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  article.content
                    ?.replace(/&nbsp;/g, ' ')
                    ?.replace(/&#39;/g, "'")
                    ?.replace(
                      /<a href="(https:\/\/www\.youtube\.com\/embed\/[^"]+)"[^>]*>[^<]*<\/a>/g,
                      '<div class="video-container"><iframe width="100%" height="400" src="$1" frameborder="0" allowfullscreen></iframe></div>'
                    )
                    ?.replace(
                      /<a href="(https:\/\/www\.youtube\.com\/watch\?v=([^"&]+))[^"]*"[^>]*>[^<]*<\/a>/g,
                      '<div class="video-container"><iframe width="100%" height="400" src="https://www.youtube.com/embed/$2" frameborder="0" allowfullscreen></iframe></div>'
                    )
                    ?.replace(
                      /<a href="(https:\/\/youtu\.be\/([^"?]+))[^"]*"[^>]*>[^<]*<\/a>/g,
                      '<div class="video-container"><iframe width="100%" height="400" src="https://www.youtube.com/embed/$2" frameborder="0" allowfullscreen></iframe></div>'
                    ) ?? '',
                  {
                    ALLOWED_TAGS: ['p','b','i','ul','ol','li','br','strong','em','h2','h3','a','div','iframe','img','span','h1','h4','table','thead','tbody','tr','th','td','section','header'],
                    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'width', 'height', 'frameborder', 'allowfullscreen', 'class', 'alt', 'style']
                  }
                ),
              }}
            />
          </div>
        </Card>

        {/* CTA Inscription */}
        <Card className="mt-8 p-8 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 border-2 border-cyan-200 dark:border-cyan-800 text-center">
          <Sparkles className="w-10 h-10 text-cyan-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Allez plus loin avec Remadia
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-lg mx-auto">
            Accédez à notre assistant IA santé, analysez vos prises de sang, recherchez des
            médicaments et bien plus encore.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-3 rounded-xl text-lg"
            >
              Créer un compte gratuit
              <Sparkles className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-2 border-gray-300 dark:border-gray-600 px-8 py-3 rounded-xl"
            >
              Se connecter
            </Button>
          </div>
        </Card>

        {/* Fonctionnalités */}
        <div className="grid md:grid-cols-3 gap-4 mt-6 mb-12">
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <MessageCircle className="w-8 h-8 text-blue-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                Chat IA Santé
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">Posez vos questions santé</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <FileSearch className="w-8 h-8 text-red-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                Analyse de Sang
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">Comprenez vos résultats</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <Phone className="w-8 h-8 text-green-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Urgences</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">Numéros et défibrillateurs</p>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
};

// ─── Navbar publique ────────────────────────────────────
const PublicNav = () => {
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 px-4 sm:px-6 lg:px-10 py-3 bg-white/80 dark:bg-gray-950/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <Link to="/articles" className="flex items-center gap-2">
          <img src="/RemaH.png" alt="Remadia" className="w-9 h-9 object-contain" />
          <span className="text-lg font-bold text-gray-900 dark:text-white">Remadia Health</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/articles"
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors hidden sm:block"
          >
            Articles
          </Link>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-5 py-2 rounded-xl text-sm font-semibold"
          >
            <span className="hidden sm:inline">Connexion / Inscription</span>
            <span className="sm:hidden">Connexion</span>
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default PublicArticlePage;
